import React from 'react';
import { Button } from '@/components/ui/button';

// Property images
const IMAGES = {
  splash: 'https://images.pexels.com/photos/19075387/pexels-photo-19075387.jpeg',
  aspen: 'https://images.pexels.com/photos/7746550/pexels-photo-7746550.jpeg',
  malibu: 'https://images.pexels.com/photos/28054849/pexels-photo-28054849.jpeg',
  caribbean: 'https://images.pexels.com/photos/12720671/pexels-photo-12720671.jpeg',
  alps: 'https://images.pexels.com/photos/32361209/pexels-photo-32361209.jpeg',
  modern: 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
  beach: 'https://images.pexels.com/photos/19879683/pexels-photo-19879683.jpeg',
  luxury: 'https://images.pexels.com/photos/17174768/pexels-photo-17174768.jpeg',
};

// VANTAGE Logo Component
export const VantageLogo = ({ size = 'default', className = '' }) => {
  const sizes = {
    sm: 'text-lg',
    default: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl',
  };
  
  return (
    <div className={`font-serif tracking-[0.3em] uppercase ${sizes[size]} ${className}`}>
      <span className="text-foreground">Van</span>
      <span className="text-primary">tage</span>
    </div>
  );
};

// Mobile Screen 1: Immersive Splash
export const MobileSplash = () => {
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      {/* Full-screen Hero Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.splash})` }}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-6 pb-12">
        {/* Glass Card */}
        <div className="glass-heavy rounded-3xl p-8 animate-fade-up">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <VantageLogo size="lg" />
          </div>
          
          {/* Headline */}
          <h1 className="font-serif text-3xl text-center text-foreground mb-3 leading-tight">
            Own the exceptional.
          </h1>
          
          {/* Subtext */}
          <p className="text-muted-foreground text-center text-sm mb-8 font-sans">
            Fractional ownership in the world&apos;s most coveted destinations.
          </p>
          
          {/* CTA Button */}
          <Button variant="goldOutline" size="lg" className="w-full">
            Explore Collection
          </Button>
        </div>
      </div>
    </div>
  );
};

// Mobile Screen 2: Home Discovery Feed
export const MobileDiscovery = () => {
  const properties = [
    {
      id: 1,
      location: 'ASPEN, COLORADO',
      name: 'The Summit Chalet',
      price: '$125k',
      image: IMAGES.aspen,
    },
    {
      id: 2,
      location: 'MALIBU, CALIFORNIA',
      name: 'Pacific Edge Villa',
      price: '$185k',
      image: IMAGES.malibu,
    },
  ];
  
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      {/* Status Bar Area */}
      <div className="h-12" />
      
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <VantageLogo size="sm" />
        <button className="w-10 h-10 rounded-full glass flex items-center justify-center">
          <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </div>
      
      {/* Property Cards - Snap Scroll */}
      <div className="flex-1 overflow-y-auto snap-y snap-mandatory px-4 pb-6 space-y-4">
        {properties.map((property, index) => (
          <div 
            key={property.id}
            className="relative h-[calc(100vh-200px)] min-h-[500px] snap-start rounded-3xl overflow-hidden property-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Property Image */}
            <img 
              src={property.image} 
              alt={property.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
            
            {/* Content Overlay */}
            <div className="absolute inset-0 p-6 flex flex-col justify-between">
              {/* Location Badge */}
              <div className="self-start">
                <span className="text-2xs uppercase tracking-[0.2em] text-foreground/80 font-semibold bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  {property.location}
                </span>
              </div>
              
              {/* Bottom Info */}
              <div className="flex items-end justify-between">
                <h2 className="font-serif text-2xl text-foreground leading-tight">
                  {property.name}
                </h2>
                <span className="text-primary font-semibold text-lg">
                  {property.price}<span className="text-muted-foreground text-sm font-normal"> / share</span>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Mobile Screen 3: Property Details (Parallax)
export const MobilePropertyDetails = () => {
  const amenities = [
    { icon: '✧', label: 'Hot Tub' },
    { icon: '⛷', label: 'Ski-In Access' },
    { icon: '◈', label: 'Concierge' },
  ];
  
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      {/* Hero Image - Top Half */}
      <div className="relative h-[45%]">
        <img 
          src={IMAGES.aspen} 
          alt="The Summit Chalet"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        
        {/* Back Button */}
        <button className="absolute top-14 left-4 w-10 h-10 rounded-full glass flex items-center justify-center">
          <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
      
      {/* Content Card - Slides Up */}
      <div className="relative -mt-12 h-[60%] glass-heavy rounded-t-[2rem] p-6 overflow-y-auto">
        {/* Location */}
        <span className="text-2xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
          ASPEN, COLORADO
        </span>
        
        {/* Property Name */}
        <h1 className="font-serif text-3xl text-foreground mt-2 mb-6">
          The Summit Chalet
        </h1>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span className="text-xs text-muted-foreground">Annual Yield</span>
            </div>
            <span className="text-2xl font-semibold text-primary">8.5%</span>
          </div>
          
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-xs text-muted-foreground">Appreciation</span>
            </div>
            <span className="text-2xl font-semibold text-primary">12%</span>
          </div>
        </div>
        
        {/* Amenities */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Amenities</h3>
          <div className="flex gap-3">
            {amenities.map((amenity) => (
              <div key={amenity.label} className="glass rounded-lg px-4 py-2 flex items-center gap-2">
                <span className="text-primary">{amenity.icon}</span>
                <span className="text-xs text-foreground">{amenity.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* CTA */}
        <Button variant="gold" size="lg" className="w-full">
          View Investment Details
        </Button>
      </div>
    </div>
  );
};

// Mobile Screen 4: Investment Calculator
export const MobileCalculator = () => {
  // Fixed share increments: 1/8 = $125k, 2/8 = $250k, 3/8 = $375k, 4/8 = $500k
  const shareOptions = [125000, 250000, 375000, 500000];
  const shareLabels = ['1/8', '2/8', '3/8', '4/8'];
  const [shareIndex, setShareIndex] = React.useState(2); // Default to 3/8 share ($375k)
  const investment = shareOptions[shareIndex];
  const estimatedIncome = Math.round(investment * 0.085);
  
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      {/* Header */}
      <div className="h-12" />
      <div className="px-6 py-4 flex items-center gap-4">
        <button className="w-10 h-10 rounded-full glass flex items-center justify-center">
          <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-serif text-xl text-foreground">Investment Calculator</h1>
      </div>
      
      {/* Property Preview */}
      <div className="px-6 mb-8">
        <div className="glass rounded-2xl p-4 flex items-center gap-4">
          <img 
            src={IMAGES.aspen} 
            alt="The Summit Chalet"
            className="w-16 h-16 rounded-xl object-cover"
          />
          <div>
            <span className="text-2xs uppercase tracking-[0.15em] text-muted-foreground">ASPEN, COLORADO</span>
            <h2 className="font-serif text-lg text-foreground">The Summit Chalet</h2>
          </div>
        </div>
      </div>
      
      {/* Calculator Content */}
      <div className="px-6 flex-1">
        {/* Share Selection */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-muted-foreground">Select Your Share</span>
            <span className="text-sm text-primary font-medium">{shareLabels[shareIndex]} Share</span>
          </div>
          
          {/* Slider Labels */}
          <div className="flex justify-between mb-3">
            <div className="text-left">
              <span className="text-xs text-muted-foreground block">1/8 Share</span>
              <span className="text-sm text-foreground font-medium">$125k</span>
            </div>
            <div className="text-right">
              <span className="text-xs text-muted-foreground block">4/8 Share</span>
              <span className="text-sm text-foreground font-medium">$500k</span>
            </div>
          </div>
          
          {/* Gold Slider - Fixed increments */}
          <div className="py-4">
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={shareIndex}
              onChange={(e) => setShareIndex(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, hsl(45 65% 52%) ${(shareIndex / 3) * 100}%, hsl(0 0% 18%) ${(shareIndex / 3) * 100}%)`,
              }}
            />
          </div>
          
          {/* Share markers */}
          <div className="flex justify-between text-2xs text-muted-foreground">
            {shareLabels.map((label, i) => (
              <button 
                key={i} 
                onClick={() => setShareIndex(i)}
                className={`transition-colors ${shareIndex === i ? 'text-primary' : 'hover:text-foreground'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Investment Amount */}
        <div className="glass-heavy rounded-2xl p-6 mb-6">
          <span className="text-sm text-muted-foreground block mb-2">Your Investment</span>
          <span className="text-4xl font-serif text-primary">
            ${investment.toLocaleString()}
          </span>
        </div>
        
        {/* Estimated Returns */}
        <div className="glass rounded-2xl p-6">
          <span className="text-sm text-muted-foreground block mb-2">Estimated Annual Rental Income</span>
          <span className="text-3xl font-serif text-foreground">
            ${estimatedIncome.toLocaleString()}
          </span>
          <span className="text-xs text-primary ml-2">+8.5% yield</span>
        </div>
      </div>
      
      {/* Bottom CTA */}
      <div className="p-6">
        <Button variant="gold" size="lg" className="w-full">
          Proceed to Investment
        </Button>
      </div>
    </div>
  );
};

// Mobile Screen 5: Portfolio Dashboard
export const MobilePortfolio = () => {
  const portfolioValue = 435000;
  const totalReturn = 12.4;
  
  const assets = [
    { name: 'The Summit Chalet', location: 'Aspen', value: 145000, return: 8.5, image: IMAGES.aspen },
    { name: 'Pacific Edge Villa', location: 'Malibu', value: 185000, return: 14.2, image: IMAGES.malibu },
    { name: 'Azure Bay Estate', location: 'Caribbean', value: 105000, return: 11.1, image: IMAGES.caribbean },
  ];
  
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      {/* Header */}
      <div className="h-12" />
      <div className="px-6 py-4 flex items-center justify-between">
        <VantageLogo size="sm" />
        <div className="flex items-center gap-3">
          <button className="w-10 h-10 rounded-full glass flex items-center justify-center">
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Portfolio Value Card */}
      <div className="px-6 mb-6">
        <div className="glass-heavy rounded-2xl p-6">
          <span className="text-sm text-muted-foreground block mb-1">Total Portfolio Value</span>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-4xl font-serif text-foreground">${portfolioValue.toLocaleString()}</span>
            <span className="text-sm text-primary font-medium">+{totalReturn}%</span>
          </div>
          
          {/* Mini Chart - Static/Decorative */}
          <div className="h-20 relative">
            <svg viewBox="0 0 300 80" className="w-full h-full">
              <defs>
                <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(45 65% 52%)" stopOpacity="0.3"/>
                  <stop offset="100%" stopColor="hsl(45 65% 52%)" stopOpacity="0"/>
                </linearGradient>
              </defs>
              <path 
                d="M0,60 Q30,50 60,55 T120,40 T180,45 T240,25 T300,20" 
                fill="none" 
                stroke="hsl(45 65% 52%)" 
                strokeWidth="2"
                className="animate-fade-in"
              />
              <path 
                d="M0,60 Q30,50 60,55 T120,40 T180,45 T240,25 T300,20 V80 H0 Z" 
                fill="url(#chartGradient)"
                className="animate-fade-in"
              />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Assets List */}
      <div className="px-6">
        <h2 className="font-serif text-lg text-foreground mb-4">Your Assets</h2>
        <div className="space-y-3">
          {assets.map((asset) => (
            <div key={asset.name} className="glass rounded-xl p-4 flex items-center gap-4">
              <img 
                src={asset.image} 
                alt={asset.name}
                className="w-14 h-14 rounded-lg object-cover"
              />
              <div className="flex-1">
                <h3 className="font-serif text-foreground">{asset.name}</h3>
                <span className="text-xs text-muted-foreground">{asset.location}</span>
              </div>
              <div className="text-right">
                <span className="text-foreground font-medium block">${asset.value.toLocaleString()}</span>
                <span className="text-xs text-primary">+{asset.return}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Mobile Screen 6: Owner's Concierge
export const MobileConcierge = () => {
  const [showServices, setShowServices] = React.useState(false);
  
  const services = [
    { icon: '🍽', label: 'Private Chef' },
    { icon: '🛒', label: 'Stock Fridge' },
    { icon: '✈', label: 'Airport Transfer' },
  ];
  
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dates = [15, 16, 17, 18, 19, 20, 21];
  
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      {/* Header */}
      <div className="h-12" />
      <div className="px-6 py-4 flex items-center gap-4">
        <button className="w-10 h-10 rounded-full glass flex items-center justify-center">
          <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-serif text-xl text-foreground">Owner&apos;s Concierge</h1>
      </div>
      
      {/* Property */}
      <div className="px-6 mb-6">
        <div className="glass rounded-2xl p-4 flex items-center gap-4">
          <img 
            src={IMAGES.aspen} 
            alt="The Summit Chalet"
            className="w-16 h-16 rounded-xl object-cover"
          />
          <div>
            <span className="text-2xs uppercase tracking-[0.15em] text-muted-foreground">ASPEN, COLORADO</span>
            <h2 className="font-serif text-lg text-foreground">The Summit Chalet</h2>
          </div>
        </div>
      </div>
      
      {/* Calendar */}
      <div className="px-6 mb-6">
        <div className="glass-heavy rounded-2xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-serif text-lg text-foreground">December 2024</h3>
            <div className="flex gap-2">
              <button className="w-8 h-8 rounded-full glass flex items-center justify-center">
                <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button className="w-8 h-8 rounded-full glass flex items-center justify-center">
                <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Days Row */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {days.map((day, i) => (
              <span key={i} className="text-center text-xs text-muted-foreground">{day}</span>
            ))}
          </div>
          
          {/* Dates Row */}
          <div className="grid grid-cols-7 gap-2">
            {dates.map((date, i) => (
              <button 
                key={i}
                className={`h-10 rounded-lg flex items-center justify-center text-sm transition-all ${
                  date >= 18 && date <= 20 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-charcoal-lighter text-foreground'
                }`}
              >
                {date}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Book Stay Button */}
      <div className="px-6">
        <Button variant="gold" size="lg" className="w-full">
          Schedule Your Stay
        </Button>
      </div>
      
      {/* FAB */}
      <div className="absolute bottom-24 right-6">
        <button 
          onClick={() => setShowServices(!showServices)}
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-gold-strong flex items-center justify-center transition-all hover:scale-105"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>
        
        {/* Services Menu */}
        {showServices && (
          <div className="absolute bottom-16 right-0 glass-heavy rounded-xl p-2 animate-scale-in">
            {services.map((service, i) => (
              <button 
                key={i}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-charcoal-lighter transition-all"
              >
                <span className="text-xl">{service.icon}</span>
                <span className="text-sm text-foreground whitespace-nowrap">{service.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile Screen 7: Filter Overlay
export const MobileFilters = () => {
  const vibes = ['Modern', 'Rustic', 'Beachfront', 'Mountain', 'Urban'];
  const regions = ['Alps', 'Caribbean', 'Pacific Coast', 'Mediterranean', 'Rockies'];
  const [selectedVibes, setSelectedVibes] = React.useState(['Modern']);
  const [selectedRegion, setSelectedRegion] = React.useState('Alps');
  const [priceRange, setPriceRange] = React.useState([100, 400]);
  
  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Background with blur - simulating overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.malibu})` }}
      >
        <div className="absolute inset-0 bg-background/80 backdrop-blur-xl" />
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col">
        {/* Header */}
        <div className="h-12" />
        <div className="px-6 py-4 flex items-center justify-between">
          <h1 className="font-serif text-xl text-foreground">Filters</h1>
          <button className="w-10 h-10 rounded-full glass flex items-center justify-center">
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Filters Content */}
        <div className="flex-1 overflow-y-auto px-6">
          {/* Vibe Chips */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-4">Property Vibe</h3>
            <div className="flex flex-wrap gap-2">
              {vibes.map((vibe) => (
                <button
                  key={vibe}
                  onClick={() => {
                    if (selectedVibes.includes(vibe)) {
                      setSelectedVibes(selectedVibes.filter(v => v !== vibe));
                    } else {
                      setSelectedVibes([...selectedVibes, vibe]);
                    }
                  }}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    selectedVibes.includes(vibe)
                      ? 'bg-primary/20 border border-primary/50 text-primary'
                      : 'glass text-foreground hover:border-primary/30'
                  }`}
                >
                  {vibe}
                </button>
              ))}
            </div>
          </div>
          
          {/* Regions - Horizontal Scroll */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-4">Region</h3>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6">
              {regions.map((region) => (
                <button
                  key={region}
                  onClick={() => setSelectedRegion(region)}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-xl text-sm transition-all ${
                    selectedRegion === region
                      ? 'bg-primary text-primary-foreground shadow-gold'
                      : 'glass text-foreground hover:border-primary/30'
                  }`}
                >
                  {region}
                </button>
              ))}
            </div>
          </div>
          
          {/* Price Range */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-foreground">Investment Range</h3>
              <span className="text-sm text-primary">${priceRange[0]}k - ${priceRange[1]}k</span>
            </div>
            
            {/* Dual Slider Visual */}
            <div className="relative py-4">
              <div className="h-2 bg-charcoal-lighter rounded-full">
                <div 
                  className="absolute h-2 rounded-full bg-gradient-to-r from-primary to-gold-bright"
                  style={{ 
                    left: `${(priceRange[0] / 500) * 100}%`, 
                    right: `${100 - (priceRange[1] / 500) * 100}%` 
                  }}
                />
              </div>
              <div 
                className="absolute top-2 w-5 h-5 -mt-1.5 rounded-full bg-primary border-2 border-gold-bright shadow-gold-strong cursor-pointer"
                style={{ left: `calc(${(priceRange[0] / 500) * 100}% - 10px)` }}
              />
              <div 
                className="absolute top-2 w-5 h-5 -mt-1.5 rounded-full bg-primary border-2 border-gold-bright shadow-gold-strong cursor-pointer"
                style={{ left: `calc(${(priceRange[1] / 500) * 100}% - 10px)` }}
              />
            </div>
          </div>
          
          {/* Yield Filter */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-4">Minimum Yield</h3>
            <div className="flex gap-2">
              {['5%', '7%', '9%', '12%+'].map((yield_val) => (
                <button
                  key={yield_val}
                  className="flex-1 py-2.5 rounded-lg glass text-sm text-foreground hover:border-primary/30 transition-all"
                >
                  {yield_val}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Bottom CTA */}
        <div className="p-6 pt-0">
          <Button variant="gold" size="lg" className="w-full">
            Show Properties
          </Button>
        </div>
      </div>
    </div>
  );
};

export { IMAGES };
